"use client";

import { ArrowLeft, ArrowRight } from "lucide-react";
import Link from "next/link";
import { Badge } from "@/components/ui/Badge";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { RichHtml } from "@/components/ui/RichHtml";
import { DynamicVideoPlayer } from "@/components/player/DynamicVideoPlayer";
import { langPath, type Lang } from "@/lib/i18n";
import { useDict } from "@/components/i18n/DictProvider";
import { videoBoxStyle, videoStreamUrl } from "@/lib/lecturehub/video";
import type { EventEntity, EventItem } from "@/lib/lecturehub/types";
import { isRecording, orderedItems, recordingSubtitle, recordingTitle } from "./eventRecordings";

export function EventRecordingPlayer({
  event,
  item,
  lang,
  watchBase,
  detailHref,
  moduleName,
  isAuthed,
}: {
  event: EventEntity | null | undefined;
  item: EventItem;
  lang: Lang;
  watchBase: string;
  detailHref: string;
  moduleName: string;
  /** Members' trail starts at the dashboard, a guest's at the home page. */
  isAuthed: boolean;
}) {
  const t = useDict();

  // Prev/next walk the recordings only — the playlist behaves like YouTube:
  // skipping straight from one watchable video to the next.
  const recordings = orderedItems(event).filter(isRecording);
  const idx = recordings.findIndex((r) => r.id === item.id);
  const prev = idx > 0 ? recordings[idx - 1] : null;
  const next = idx >= 0 && idx < recordings.length - 1 ? recordings[idx + 1] : null;

  const title = recordingTitle(item);
  const subtitle = recordingSubtitle(item);
  const lec = item.lecture;
  const v = lec?.video ?? null;
  const stream = videoStreamUrl(v);
  const playable = !!v && v.processed !== false && !!stream;
  const stillProcessing = !!v && v.processed === false;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Breadcrumb
          items={[
            isAuthed
              ? { href: langPath(lang, "/profil"), label: t.appNav.dashboard }
              : { href: langPath(lang, "/"), label: t.nav.home },
            { href: detailHref, label: moduleName },
            { label: title },
          ]}
        />
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Badge tone="gold">{t.detail.typeRecording}</Badge>
        </div>
        <h1 className="mt-3 !text-[22px] md:!text-[28px] !font-semibold !tracking-tight !leading-tight !text-foreground">
          {title}
        </h1>
        {subtitle ? <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p> : null}
      </div>

      {playable ? (
        <div
          data-section="event-recording-video"
          className="rounded-2xl overflow-hidden shadow-soft"
        >
          <DynamicVideoPlayer
            src={stream}
            poster={v.image?.uri ?? null}
            width={v.width}
            height={v.height}
            subtitles={v.subtitles}
            title={title}
            chapters={v.chapters}
            duration={v.duration}
            lang={lang}
          />
        </div>
      ) : stillProcessing ? (
        <div
          data-section="event-recording-processing"
          style={videoBoxStyle(v)}
          className="w-full rounded-2xl bg-foreground/90 text-background/70 grid place-items-center"
        >
          <p className="text-sm">{t.detail.processing}</p>
        </div>
      ) : (
        <div
          style={videoBoxStyle(v)}
          className="w-full rounded-2xl bg-muted grid place-items-center"
        >
          <p className="text-sm text-muted-foreground">{t.detail.recordingUnavailable}</p>
        </div>
      )}

      {lec?.abstract || lec?.description ? (
        <section
          data-section="event-recording-overview"
          className="course-section rounded-card p-6 md:p-8 bg-card border border-border/60"
        >
          <h2 className="!text-[16px] md:!text-[18px] !font-semibold !leading-tight !text-foreground !mb-4">
            {t.detail.aboutRecording}
          </h2>
          {lec.abstract ? (
            <RichHtml
              html={lec.abstract}
              className="relative pl-5 border-l-2 border-accent/70 text-[15px] md:text-[17px] leading-relaxed text-foreground/85"
            />
          ) : null}
          {lec.description ? (
            <div className={lec.abstract ? "mt-5" : undefined}>
              <RichHtml html={lec.description} />
            </div>
          ) : null}
        </section>
      ) : null}

      {prev || next ? (
        <nav className="mt-2 flex items-stretch justify-between gap-3 border-t border-border/60 pt-5">
          {prev ? (
            <Link
              href={`${watchBase}/${prev.id}`}
              className="group flex-1 min-w-0 inline-flex items-center gap-2 text-left"
            >
              <ArrowLeft
                className="size-4 shrink-0 text-muted-foreground group-hover:text-accent"
                aria-hidden
              />
              <span className="min-w-0">
                <span className="block text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground">
                  {t.detail.prevItem}
                </span>
                <span className="block truncate text-sm font-display font-bold text-foreground group-hover:text-accent">
                  {recordingTitle(prev)}
                </span>
              </span>
            </Link>
          ) : (
            <span className="flex-1" />
          )}
          {next ? (
            <Link
              href={`${watchBase}/${next.id}`}
              className="group flex-1 min-w-0 inline-flex items-center justify-end gap-2 text-right"
            >
              <span className="min-w-0">
                <span className="block text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground">
                  {t.detail.nextItem}
                </span>
                <span className="block truncate text-sm font-display font-bold text-foreground group-hover:text-accent">
                  {recordingTitle(next)}
                </span>
              </span>
              <ArrowRight
                className="size-4 shrink-0 text-muted-foreground group-hover:text-accent"
                aria-hidden
              />
            </Link>
          ) : (
            <span className="flex-1" />
          )}
        </nav>
      ) : null}
    </div>
  );
}
